/**
 * Wishlist — ♡ 保存トグル + ヘッダー導線 + ドロワー (ARK engage Phase 2)
 *
 * Storage / contract:
 *   localStorage["ark.wishlist"] = [{ productId, variantId, handle, title, image, price, currency }, ...]
 *     → ドロワー描画用の「表示キャッシュ」。engage は id しか返さないため theme 側で保持する。
 *   sessionStorage["ark.wishlist.merged"] = "1"  → ログイン後の /merge を 1 セッション 1 回に絞る。
 *
 * State:
 *   savedIds = Set<variantId(string)>  → ♡ 状態と件数バッジのソース。
 *     - ゲスト: localStorage キャッシュから導出。
 *     - ログイン: engage GET が正 (load 時に照合)。
 *
 * Events:
 *   ark:wishlist-changed  (dispatch/listen)  detail: { count, savedIds }
 *   ark:wishlist-open     (listen)           → ドロワーを開く (ヘッダー ♡ が dispatch)
 *
 * Backend (App Proxy, 既定 /apps/ark-engage/wishlist):
 *   GET    → { success, data:[{productId, variantId, alertState, targetJpy}] }
 *   POST   { productId, variantId } → 201 added / 200 exists / 409 wishlist_full
 *   DELETE ?variantId=<id>          → { success, data:{ removed } }
 *   POST /merge { items:[{productId, variantId}] } → { success, data:{ merged, skipped } }
 *   未ログインは 401 { error:"login_required" }。credentials:"same-origin"。
 *
 * Phase 3 価格アラート (FX 希望 JPY・ログイン必須):
 *   GET は item 毎に alertState (NONE/WATCHING/TRIGGERED/NOTIFIED) と targetJpy を同梱。
 *   POST   /alert { variantId, targetJpy } → 201/200 設定 (WATCHING)。404 item_not_found /
 *                                            503 base_price_unavailable / 400 invalid。
 *   DELETE /alert?variantId=<id>           → 解除 (NONE)。
 *   in-memory alertByVariant[variantId] = { state, targetJpy } (server GET が正・楽観更新)。
 *
 * ログイン: optimistic UI → 失敗 (!res.ok) はロールバック + トースト。
 * ゲスト: サーバを叩かず localStorage のみ。ログイン検知で /merge を 1 回。alert 不可 (hint のみ)。
 */
(function () {
  'use strict';
  if (window.__arkWishlistLoaded) return;
  window.__arkWishlistLoaded = true;

  var CFG = window.ARK_WISHLIST || {};
  var PROXY_BASE = (CFG.proxyBase || '/apps/ark-engage/wishlist').replace(/\/$/, '');
  var LOGGED_IN = !!CFG.loggedIn;
  var LIMIT = CFG.limit || 250;

  // ---- Phase 3 価格アラート (FX 希望 JPY) ----
  // alertEnabled が明示 false のときだけ無効化 (未設定は既定 on)。off で全アラート UI を非表示 (rollback)。
  // 価格アラートは「ログイン必須」。ゲストは localStorage のみで alert 不可 (控えめな hint のみ)。
  var ALERT_ENABLED = CFG.alertEnabled !== false;
  var ALERT_LOGIN_URL = CFG.loginUrl || '/account/login';
  // 商品リンクの起点。locale-prefixed ストアで '/products/...' を直書きすると
  // ロケールが落ちるので、Liquid の routes.root_url を通す (primary では '/')。
  var ROOT_URL = (CFG.rootUrl || '/').replace(/\/$/, '');
  // targetJpy の上限 (濫用防止・整数 JPY)。サーバ側でも検証されるが UI で先に弾く。
  var ALERT_MAX_JPY = 100000000;
  // 目標価格の下限比率 (現在の表示 JPY に対する)。engage の
  // web/lib/wishlist/validation.ts ALERT_TARGET_MIN_RATIO と同値に保つこと。
  // 到達判断はサーバが最終権威で、ここは入力前に弾いて無駄な往復を減らすためのもの。
  var ALERT_TARGET_MIN_RATIO = 0.7;
  // プリセットの割引率。為替変動で現実的に到達し得る幅に絞る。
  var ALERT_PRESET_RATIOS = [0.03, 0.05, 0.1];
  // プリセット金額の丸め単位 (円)。端数を出さず選びやすくする。
  var ALERT_PRESET_STEP = 100;
  // 各 variant の alert 状態を保持する in-memory マップ:
  //   { [variantId]: { state, targetJpy, startJpy } }  startJpy = 設定時の表示 JPY。
  // server GET (reconcile) が正。ローカル set/clear で楽観更新し、次の reconcile で確定する。
  var alertByVariant = {};
  // server が返す「現在の表示 JPY」({ [variantId]: number })。JP market の contextualPricing 由来で、
  // localStorage キャッシュ (閲覧者の market 通貨) より優先する。
  var currentJpyByVariant = {};

  // ---- Phase 2.5 適合バッジ (fitment) ----
  // fitmentEnabled が明示 false のときだけ無効化 (未設定は既定 on)。off で全バッジ・誘導を完全非表示 (rollback)。
  var FITMENT_ENABLED = CFG.fitmentEnabled !== false;
  // Direct CORS (window.ARK_API_BASE = PIM 公開 URL)。未設定時のみ App Proxy フォールバック (custom-fitment-product-badge.js と同条件)。
  var FITMENT_API_BASE = (typeof window !== 'undefined' && window.ARK_API_BASE) || '/apps/fitment';
  var FITMENT_SHOP_DOMAIN = typeof window !== 'undefined' ? window.ARK_SHOP_DOMAIN || null : null;
  var VEHICLE_STORAGE_KEY = 'ark.selectedVehicle';
  var VEHICLE_COOKIE_KEY = 'ark_vehicle';
  var VEHICLE_CHANGED_EVENT = 'ark:vehicle-changed';
  var VEHICLE_CHANGE_REQUEST_EVENT = 'ark:vehicle-change-request';

  var STORAGE_KEY = 'ark.wishlist';
  var MERGED_FLAG = 'ark.wishlist.merged';
  var CHANGED_EVENT = 'ark:wishlist-changed';
  var OPEN_EVENT = 'ark:wishlist-open';
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var POP_MS = 360;
  var TOAST_MS = 3200;

  var MSG_FAIL = '保存に失敗しました。もう一度お試しください';
  var MSG_FULL = 'ウィッシュリストの上限（' + LIMIT + '件）に達しています';
  var MSG_ALERT_FAIL = '価格アラートを設定できませんでした。もう一度お試しください';
  var MSG_ALERT_BASEPRICE = '現在の価格を取得できませんでした。時間をおいて再度お試しください';
  var MSG_ALERT_INVALID = '目標価格を正しく入力してください';
  var MSG_ALERT_NOT_BELOW = '目標価格は現在価格より低い金額にしてください';
  // 下限は現在価格に依存するので文言を組み立てる (為替では届かない安値を弾く)。
  function floorMessage(floorJpy) {
    return '目標価格は ' + formatJpy(floorJpy) + ' 以上で設定してください';
  }
  var MSG_ALERT_CLEAR_FAIL = '価格アラートを解除できませんでした。もう一度お試しください';

  var savedIds = new Set();

  var DBG = function () {
    if (window.ARK_DEBUG !== false) {
      console.log.apply(console, ['[ark/wishlist]'].concat(Array.prototype.slice.call(arguments)));
    }
  };

  // ---- shared body scroll lock (garage widget と共有) ----
  if (!window.__arkScrollLock) {
    window.__arkScrollLock = {
      count: 0,
      prev: null,
      prevPad: null,
      lock: function () {
        if (this.count === 0) {
          // スクロールバーが占めていた幅を padding で埋め戻す。
          // これが無いと overflow:hidden にした瞬間にバーの幅ぶん
          // ページ全体が横にずれる (desktop で顕著)。
          var gap = window.innerWidth - document.documentElement.clientWidth;
          this.prev = document.body.style.overflow;
          this.prevPad = document.body.style.paddingRight;
          document.body.style.overflow = 'hidden';
          if (gap > 0) {
            var current = parseFloat(window.getComputedStyle(document.body).paddingRight);
            document.body.style.paddingRight = (isFinite(current) ? current : 0) + gap + 'px';
          }
        }
        this.count++;
      },
      unlock: function () {
        this.count = Math.max(0, this.count - 1);
        if (this.count === 0) {
          document.body.style.overflow = this.prev || '';
          document.body.style.paddingRight = this.prevPad || '';
          this.prev = null;
          this.prevPad = null;
        }
      },
    };
  }

  // ---- sanitize / storage ----
  function toIdString(v) {
    if (v == null) return '';
    var s = String(v).trim();
    // 数値 id (Shopify) のみ許可。想定外文字列は弾く。
    return /^[0-9]{1,20}$/.test(s) ? s : '';
  }

  function sanitizeItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var productId = toIdString(raw.productId);
    var variantId = toIdString(raw.variantId);
    if (!productId || !variantId) return null;
    var price = parseInt(raw.price, 10);
    return {
      productId: productId,
      variantId: variantId,
      handle: typeof raw.handle === 'string' ? raw.handle.slice(0, 200) : '',
      title: typeof raw.title === 'string' ? raw.title.slice(0, 300) : '',
      image: typeof raw.image === 'string' ? raw.image.slice(0, 1000) : '',
      price: isFinite(price) ? price : null,
      currency: typeof raw.currency === 'string' ? raw.currency.slice(0, 8) : '',
    };
  }

  function loadCache() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return [];
      var out = [];
      for (var i = 0; i < arr.length; i++) {
        var s = sanitizeItem(arr[i]);
        if (s) out.push(s);
      }
      return out;
    } catch {
      return [];
    }
  }

  function saveCache(arr) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch {
      /* storage full / disabled — 無視 (savedIds は in-memory で維持) */
    }
  }

  function addToCache(item) {
    var arr = loadCache().filter(function (i) {
      return i.variantId !== item.variantId;
    });
    arr.unshift(item);
    saveCache(arr);
  }

  function removeFromCache(variantId) {
    saveCache(
      loadCache().filter(function (i) {
        return i.variantId !== variantId;
      })
    );
  }

  function cacheEntry(variantId) {
    var arr = loadCache();
    for (var i = 0; i < arr.length; i++) {
      if (arr[i].variantId === variantId) return arr[i];
    }
    return null;
  }

  // ---- state helpers ----
  function isSaved(variantId) {
    return savedIds.has(toIdString(variantId));
  }

  function emitChanged() {
    window.dispatchEvent(
      new CustomEvent(CHANGED_EVENT, {
        detail: { count: savedIds.size, savedIds: Array.from(savedIds) },
      })
    );
  }

  function seedFromCache() {
    savedIds = new Set(
      loadCache().map(function (i) {
        return i.variantId;
      })
    );
  }

  // ---- API ----
  function apiGet() {
    return fetch(PROXY_BASE, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
  }

  function apiPost(item) {
    return fetch(PROXY_BASE, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ productId: item.productId, variantId: item.variantId }),
    });
  }

  function apiDelete(variantId) {
    return fetch(PROXY_BASE + '?variantId=' + encodeURIComponent(variantId), {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
  }

  function apiMerge(items) {
    return fetch(PROXY_BASE + '/merge', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    });
  }

  // ---- Phase 3 alert API (App Proxy /apps/ark-engage/wishlist/alert・ログイン必須) ----
  function apiSetAlert(variantId, targetJpy) {
    return fetch(PROXY_BASE + '/alert', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ variantId: variantId, targetJpy: targetJpy }),
    });
  }

  function apiClearAlert(variantId) {
    return fetch(PROXY_BASE + '/alert?variantId=' + encodeURIComponent(variantId), {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
  }

  // ---- toast ----
  var toastRegion = null;
  function getToastRegion() {
    if (toastRegion && document.body.contains(toastRegion)) return toastRegion;
    toastRegion = document.createElement('div');
    toastRegion.className = 'ark-wishlist-toast-region';
    toastRegion.setAttribute('role', 'status');
    toastRegion.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastRegion);
    return toastRegion;
  }

  function toast(message) {
    var region = getToastRegion();
    var el = document.createElement('div');
    el.className = 'ark-wishlist-toast';
    el.textContent = message;
    region.appendChild(el);
    // reflow → transition in
    void el.offsetWidth;
    el.classList.add('is-visible');
    setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 250);
    }, TOAST_MS);
  }

  // ---- mutations (optimistic) ----
  function setLoading(btn, on) {
    if (btn) btn.classList.toggle('is-loading', on);
  }

  function triggerPop(btn) {
    if (!btn) return;
    btn.classList.remove('is-pop');
    void btn.offsetWidth;
    btn.classList.add('is-pop');
    setTimeout(function () {
      btn.classList.remove('is-pop');
    }, POP_MS);
  }

  /* GA4 へのイベント送出 (assets/custom-analytics.js)。
     未ロード・無効でも動作に影響しないよう存在チェックだけして呼ぶ。 */
  function track(name, params) {
    try {
      if (window.ArkAnalytics && typeof window.ArkAnalytics.track === 'function') {
        window.ArkAnalytics.track(name, params);
      }
    } catch (_) {}
  }

  /* パラメータ名は GA4 の標準 e-commerce 名 (item_id / item_name) を避ける。
     あちらは items[] 配列専用で、イベント直下に同名を置くとカスタムディメンション
     として登録できない。 */
  function wishlistEventParams(item) {
    return {
      variant_id: item.variantId,
      product_id: item.productId,
      product_title: item.title || '',
      logged_in: LOGGED_IN ? 'yes' : 'no',
      wishlist_size: savedIds.size,
    };
  }

  function save(item, btn) {
    var vid = item.variantId;
    if (savedIds.has(vid)) return;
    // optimistic
    savedIds.add(vid);
    addToCache(item);
    emitChanged();
    triggerPop(btn);
    // 楽観 UI と同じタイミングで送る。API 失敗時のロールバックは wishlist_remove
    // ではなく wishlist_add_failed として区別する (取り消し操作と混ざらないように)。
    track('wishlist_add', wishlistEventParams(item));

    if (!LOGGED_IN) return;

    setLoading(btn, true);
    apiPost(item)
      .then(function (res) {
        setLoading(btn, false);
        if (res.status === 409) {
          rollbackAdd(vid);
          toast(MSG_FULL);
          return;
        }
        if (!res.ok) {
          rollbackAdd(vid);
          toast(MSG_FAIL);
        }
      })
      .catch(function () {
        setLoading(btn, false);
        rollbackAdd(vid);
        toast(MSG_FAIL);
      });
  }

  function rollbackAdd(vid) {
    savedIds.delete(vid);
    removeFromCache(vid);
    emitChanged();
    track('wishlist_add_failed', { variant_id: vid, logged_in: LOGGED_IN ? 'yes' : 'no' });
  }

  function remove(variantId, btn) {
    var vid = toIdString(variantId);
    if (!vid || !savedIds.has(vid)) return;
    var prev = cacheEntry(vid);
    var prevAlert = alertByVariant[vid]; // 削除ロールバック用に退避
    // optimistic
    savedIds.delete(vid);
    removeFromCache(vid);
    // item 削除はサーバ側で alert も消える。楽観的に alert cache も落とす。
    // (reload せず同 variant を再追加した際に、削除済みの古い alert が active 表示される不具合を防ぐ)
    delete alertByVariant[vid];
    emitChanged();
    track('wishlist_remove', {
      variant_id: vid,
      product_id: prev ? prev.productId : '',
      product_title: prev ? prev.title || '' : '',
      logged_in: LOGGED_IN ? 'yes' : 'no',
      wishlist_size: savedIds.size,
    });

    if (!LOGGED_IN) return;

    setLoading(btn, true);
    apiDelete(vid)
      .then(function (res) {
        setLoading(btn, false);
        if (!res.ok) {
          rollbackRemove(vid, prev, prevAlert);
          toast(MSG_FAIL);
        }
      })
      .catch(function () {
        setLoading(btn, false);
        rollbackRemove(vid, prev, prevAlert);
        toast(MSG_FAIL);
      });
  }

  function rollbackRemove(vid, prev, prevAlert) {
    savedIds.add(vid);
    if (prev) addToCache(prev);
    // 削除が rollback された場合のみ alert cache を戻す (サーバ側でも削除されていないため)
    if (prevAlert) alertByVariant[vid] = prevAlert;
    emitChanged();
  }

  function toggle(item, btn) {
    if (savedIds.has(item.variantId)) remove(item.variantId, btn);
    else save(item, btn);
  }

  // ---- button wiring ----
  function itemFromButton(btn) {
    var d = btn.dataset;
    return sanitizeItem({
      productId: d.productId,
      variantId: d.variantId,
      handle: d.handle,
      title: d.title,
      image: d.image,
      price: d.price,
      currency: d.currency,
    });
  }

  function syncButtonState(btn) {
    var saved = isSaved(btn.dataset.variantId);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.setAttribute('aria-label', saved ? 'ウィッシュリストから削除' : 'ウィッシュリストに保存');
    btn.classList.toggle('is-saved', saved);
  }

  function getVariantMap(wrap) {
    if (wrap._arkVariantMap) return wrap._arkVariantMap;
    var script = wrap.querySelector('[data-ark-wishlist-variants]');
    var map = {};
    if (script) {
      try {
        var arr = JSON.parse(script.textContent);
        if (Array.isArray(arr)) {
          arr.forEach(function (v) {
            var id = toIdString(v.id);
            if (id) map[id] = { price: v.price, image: v.image };
          });
        }
      } catch {
        /* malformed — 空マップ */
      }
    }
    wrap._arkVariantMap = map;
    return map;
  }

  function updateButtonVariant(btn, newVid) {
    var vid = toIdString(newVid);
    if (!vid) return;
    btn.dataset.variantId = vid;
    var wrap = btn.closest('[data-ark-wishlist-wrap]');
    if (wrap) {
      var map = getVariantMap(wrap);
      if (map[vid]) {
        if (map[vid].price != null) btn.dataset.price = String(map[vid].price);
        if (map[vid].image != null) btn.dataset.image = map[vid].image;
      }
    }
    syncButtonState(btn);
  }

  // ♡ クリックは document への委譲で処理する (bindDelegatedClick)。boot 後に
  // product-recommendations 等が innerHTML で後挿入したカードのボタンでも動くようにするため、
  // ボタン個別の click listener は張らない。setupButton は初期状態同期と PDP 固有の
  // variant 追従配線のみを担う。
  function handleWishlistClick(e) {
    var target = e.target;
    var btn = target && target.closest ? target.closest('[data-ark-wishlist-btn]') : null;
    if (!btn) return;
    if (btn.classList.contains('is-loading')) return;
    var item = itemFromButton(btn);
    if (!item) {
      DBG('button click ignored — invalid product/variant id', btn.dataset);
      return;
    }
    toggle(item, btn);
  }

  function bindDelegatedClick() {
    if (document._arkWishlistDelegated) return;
    document._arkWishlistDelegated = true;
    document.addEventListener('click', handleWishlistClick);
  }

  function setupButton(btn) {
    if (btn._arkWishlistBound) return;
    btn._arkWishlistBound = true;
    syncButtonState(btn);

    // PDP: variant 変更に追従 ([name="id"] change + variant-selection[variant] 属性)
    if (btn.classList.contains('ark-wishlist-btn--pdp')) {
      var form = btn.closest('form') || document;
      var idField = form.querySelector("select[name='id'], input[name='id']");
      if (idField) {
        idField.addEventListener('change', function () {
          updateButtonVariant(btn, idField.value);
        });
      }
      var vsel = form.querySelector('[data-variant-selection]');
      if (vsel && 'MutationObserver' in window) {
        var mo = new MutationObserver(function () {
          updateButtonVariant(btn, vsel.getAttribute('variant'));
        });
        mo.observe(vsel, { attributes: true, attributeFilter: ['variant'] });
      }
    }
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-ark-wishlist-btn]').forEach(syncButtonState);
  }

  function updateBadges() {
    var count = savedIds.size;
    document.querySelectorAll('[data-custom-wishlist-count]').forEach(function (badge) {
      if (count === 0) {
        badge.hidden = true;
      } else {
        badge.hidden = false;
        badge.textContent = String(count);
      }
    });
  }

  function initButtons() {
    document.querySelectorAll('[data-ark-wishlist-btn]').forEach(setupButton);
  }

  // 後挿入されたボタンの初期状態 (♡ saved 表示) と PDP variant 配線を追従させる。
  // クリック自体は委譲 (bindDelegatedClick) で常に動くが、初期の見た目同期は挿入検知が要る。
  var reinitScheduled = false;
  function scheduleButtonReinit() {
    if (reinitScheduled) return;
    reinitScheduled = true;
    var run = function () {
      reinitScheduled = false;
      initButtons();
      syncAllButtons();
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
    else setTimeout(run, 0);
  }

  function observeInsertedButtons() {
    if (!('MutationObserver' in window) || !document.body) return;
    if (document.body._arkWishlistObserved) return;
    document.body._arkWishlistObserved = true;
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (!node || node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches('[data-ark-wishlist-btn]')) ||
            (node.querySelector && node.querySelector('[data-ark-wishlist-btn]'))
          ) {
            scheduleButtonReinit();
            return;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // ---- money ----
  function formatMoney(price, currency) {
    if (price == null || !isFinite(price)) return '';
    var cur = currency || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cur,
      }).format(price / 100);
    } catch {
      return (price / 100).toFixed(2) + ' ' + cur;
    }
  }

  // ---- Phase 3 alert value helpers ----
  // 目標 / あと の JPY 表示。targetJpy は「実円 (整数)」で扱う。
  function formatJpy(yen) {
    if (yen == null || !isFinite(yen)) return '';
    try {
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
        maximumFractionDigits: 0,
      }).format(yen);
    } catch {
      return '¥' + Math.round(yen).toLocaleString('ja-JP');
    }
  }

  // 計算に使える現在 JPY は **server 由来のみ**。localStorage キャッシュは保存時のスナップショット
  // (しかも閲覧者の market 通貨) なので、範囲検証・プリセット・差額・下落率の基準にすると
  // 古い値で「サーバなら通る目標」を弾いたり、ありもしない下落を表示したりする。
  function serverCurrentJpy(item) {
    if (!item) return null;
    var fromServer = currentJpyByVariant[item.variantId];
    if (fromServer == null || !isFinite(fromServer) || fromServer <= 0) return null;
    return fromServer;
  }

  // 「現在 ◯◯」の表示文字列。server の JPY を優先し、無い時だけキャッシュを**表示専用**で出す
  // (数値ロジックには渡さない)。
  function currentPriceText(item) {
    var jpy = serverCurrentJpy(item);
    if (jpy != null) return formatJpy(jpy);
    return formatMoney(item.price, item.currency);
  }

  // 目標価格の下限 (円)。境界値そのものは許可するので切り上げる。
  function alertFloorJpy(currentJpy) {
    return Math.ceil(currentJpy * ALERT_TARGET_MIN_RATIO);
  }

  // プリセット候補 (現在価格からの割引・¥100 切り捨て)。下限未満や重複は落とす。
  function presetTargets(currentJpy) {
    if (currentJpy == null || !isFinite(currentJpy) || currentJpy <= 0) return [];
    var floor = alertFloorJpy(currentJpy);
    var seen = {};
    var out = [];
    ALERT_PRESET_RATIOS.forEach(function (ratio) {
      var value = Math.floor((currentJpy * (1 - ratio)) / ALERT_PRESET_STEP) * ALERT_PRESET_STEP;
      if (value <= 0 || value >= currentJpy || value < floor) return;
      if (seen[value]) return;
      seen[value] = true;
      out.push({ ratio: ratio, value: value });
    });
    return out;
  }

  // 設定時からの下落率 (%)。上昇 / 同値 / 不明は null = 表示しない (値上げは出さない)。
  function dropSinceStart(startJpy, currentJpy) {
    if (startJpy == null || currentJpy == null) return null;
    if (!isFinite(startJpy) || !isFinite(currentJpy)) return null;
    if (startJpy <= 0 || currentJpy >= startJpy) return null;
    return {
      amount: startJpy - currentJpy,
      percent: ((startJpy - currentJpy) / startJpy) * 100,
    };
  }

  // 入力文字列 → 正の整数 JPY。数字以外を除去し、0 以下 / 上限超は null。
  function parseTargetJpy(raw) {
    if (raw == null) return null;
    var digits = String(raw).replace(/[^0-9]/g, '');
    if (!digits) return null;
    var n = parseInt(digits, 10);
    if (!isFinite(n) || n <= 0 || n > ALERT_MAX_JPY) return null;
    return n;
  }

  // 3 桁区切り (入力欄の見やすさ用)。
  function groupDigits(raw) {
    var digits = String(raw == null ? '' : raw).replace(/[^0-9]/g, '');
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // server の alertState を正規化 (未知は NONE)。
  function normalizeAlertState(state) {
    if (state === 'WATCHING' || state === 'TRIGGERED' || state === 'NOTIFIED') return state;
    return 'NONE';
  }

  function parseServerTargetJpy(v) {
    if (v == null) return null;
    var n = parseInt(v, 10);
    return isFinite(n) && n > 0 ? n : null;
  }

  // ---- fitment badge (Phase 2.5) ----
  // custom-fitment-product-badge.js の judge() / mapCheckToState() / fetchCheck() / loadVehicle() /
  // ICON_SVG を wishlist 用に移植 (元ファイルは改変しない)。3 値: 適合(緑) / 注意(灰) / 非適合(赤)。
  //   カード  = SSR metafield で 0-network 判定 (SSR 空はバッジ非表示、API を叩かない = storm 回避)。
  //   ドロワー = 開時に check_batch を 1 リクエスト、vehicleKey でセッションキャッシュ、degrade は全非表示。

  // 描画済み batch 結果を vehicleKey 単位でキャッシュ (同一 key は再 fetch しない)。
  var fitmentBatchCache = {};

  function getVehicleCookie() {
    try {
      var parts = document.cookie.split(';');
      for (var i = 0; i < parts.length; i++) {
        var kv = parts[i].split('=');
        if (kv[0].trim() === VEHICLE_COOKIE_KEY) {
          return decodeURIComponent(kv.slice(1).join('=').trim());
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  function loadVehicle() {
    try {
      if (window.ARK_INITIAL_VEHICLE) {
        var initial = window.ARK_INITIAL_VEHICLE;
        if (typeof initial === 'string') {
          try {
            return JSON.parse(initial);
          } catch {
            /* fallthrough */
          }
        } else if (typeof initial === 'object') {
          return initial;
        }
      }
      var raw = localStorage.getItem(VEHICLE_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
      var ck = getVehicleCookie();
      return ck ? JSON.parse(ck) : null;
    } catch {
      return null;
    }
  }

  function vehicleKey(v) {
    return [v.year, v.make, v.model, v.submodel || ''].join('|').toLowerCase();
  }

  function vehicleLabel(v) {
    if (!v) return '';
    var s = v.year + ' ' + v.make + ' ' + v.model;
    if (v.submodel) s += ' ' + v.submodel;
    return s;
  }

  // lucide 幾何を createElementNS で構築 (ICON_SVG 相当、innerHTML 不使用)。
  function fitmentSvg(children, width) {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', width || '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    children.forEach(function (c) {
      var el = document.createElementNS(SVG_NS, c.tag);
      Object.keys(c.attrs).forEach(function (k) {
        el.setAttribute(k, c.attrs[k]);
      });
      svg.appendChild(el);
    });
    return svg;
  }

  function fitmentIcon(name) {
    var span = document.createElement('span');
    span.className = 'ark-wishlist-fitment__icon';
    var svg;
    if (name === 'check') {
      svg = fitmentSvg([{ tag: 'polyline', attrs: { points: '20 6 9 17 4 12' } }], '2');
    } else if (name === 'x') {
      svg = fitmentSvg(
        [
          { tag: 'line', attrs: { x1: '18', y1: '6', x2: '6', y2: '18' } },
          { tag: 'line', attrs: { x1: '6', y1: '6', x2: '18', y2: '18' } },
        ],
        '2'
      );
    } else if (name === 'gear') {
      svg = fitmentSvg(
        [
          { tag: 'circle', attrs: { cx: '12', cy: '12', r: '3' } },
          {
            tag: 'path',
            attrs: {
              d: 'M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
            },
          },
        ],
        '1.75'
      );
    } else {
      // question
      svg = fitmentSvg(
        [
          { tag: 'circle', attrs: { cx: '12', cy: '12', r: '10' } },
          { tag: 'path', attrs: { d: 'M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3' } },
          { tag: 'line', attrs: { x1: '12', y1: '17', x2: '12.01', y2: '17' } },
        ],
        '1.75'
      );
    }
    span.appendChild(svg);
    return span;
  }

  // reason (check_batch / SSR judge) → wishlist の 3 値バッジ state。
  // null = 判定不能 → バッジ非表示 (誤判定回避)。
  function reasonToState(reason, fits) {
    if (reason === 'universal') return 'universal';
    // match-ymm = submodel 未選択での Y/M/M 一致 → 選択粒度では適合
    if (reason === 'exact-match' || reason === 'match-ymm') return 'fits';
    if (reason === 'match-no-submodel') return 'caution';
    if (reason === 'not-fitting') return 'not-fitting';
    if (fits) return 'fits';
    return null; // no-fitment-data / 商品不在 → 不明
  }

  function fitmentContent(state, vehicle) {
    var label = vehicleLabel(vehicle);
    // 「この車両に(は)」は毎カード同じ = 情報量ゼロなので付けない。
    // どの車両かはガレージの表示とページ上部で分かる。車両名は sub に残してあり、
    // ドロワーでは併記される (カードでは CSS で畳む)。
    if (state === 'fits') return { icon: 'check', primary: '適合', sub: label };
    if (state === 'universal')
      return { icon: 'gear', primary: '汎用パーツ', sub: '汎用（全車種）' };
    if (state === 'caution') {
      var ymm = vehicle ? vehicle.year + ' ' + vehicle.make + ' ' + vehicle.model : '';
      return { icon: 'question', primary: '要確認', sub: ymm ? ymm + '（サブモデル未確認）' : '' };
    }
    if (state === 'not-fitting') return { icon: 'x', primary: '非適合', sub: label };
    return null;
  }

  var FITMENT_STATE_CLASSES = [
    'ark-wishlist-fitment--fits',
    'ark-wishlist-fitment--universal',
    'ark-wishlist-fitment--caution',
    'ark-wishlist-fitment--not-fitting',
    'ark-wishlist-fitment--card',
    'ark-wishlist-fitment--drawer',
  ];

  function hideFitmentBadge(container) {
    FITMENT_STATE_CLASSES.forEach(function (c) {
      container.classList.remove(c);
    });
    var inner = container.querySelector('[data-wishlist-fitment-inner]');
    if (inner) while (inner.firstChild) inner.removeChild(inner.firstChild);
    container.removeAttribute('title');
    container.hidden = true;
  }

  // ドロワー / カード共通の 3 値バッジ builder。state→markup をここに集約。
  function renderFitmentBadge(container, state, vehicle, context) {
    var inner = container.querySelector('[data-wishlist-fitment-inner]');
    if (!inner) return;
    var content = fitmentContent(state, vehicle);
    if (!content) {
      hideFitmentBadge(container);
      return;
    }
    FITMENT_STATE_CLASSES.forEach(function (c) {
      container.classList.remove(c);
    });
    while (inner.firstChild) inner.removeChild(inner.firstChild);

    container.classList.add('ark-wishlist-fitment');
    container.classList.add('ark-wishlist-fitment--' + state);
    container.classList.add('ark-wishlist-fitment--' + (context || 'drawer'));

    inner.appendChild(fitmentIcon(content.icon));

    var text = document.createElement('span');
    text.className = 'ark-wishlist-fitment__text';
    text.textContent = content.primary;
    if (content.sub) {
      var sub = document.createElement('span');
      sub.className = 'ark-wishlist-fitment__vehicle';
      sub.textContent = content.sub;
      text.appendChild(sub);
    }
    inner.appendChild(text);

    // カードは overlay で狭いため vehicle 併記は CSS で畳み、full ラベルは title で担保。
    container.title = content.sub ? content.primary + ' — ' + content.sub : content.primary;
    container.hidden = false;
  }

  // カード = SSR fitment array + 選択車両で 0-network 判定。
  //   'fits' | 'universal' | 'caution' | 'not-fitting' | null(非表示)。SSR 空/未選択は null (API を叩かない)。
  function readCardSsr(host) {
    var script = host.querySelector('script[data-wishlist-fitment-ssr]');
    if (!script) return null;
    try {
      var data = JSON.parse(script.textContent);
      return Array.isArray(data) ? data : null;
    } catch {
      return null;
    }
  }

  function judgeCard(host, vehicle) {
    if (host.dataset.isUniversal === 'true') return 'universal';
    if (!vehicle) return null;
    var fitment = readCardSsr(host);
    if (!fitment || fitment.length === 0) return null; // SSR 空は非表示 (storm 回避)
    var y = parseInt(vehicle.year, 10);
    var mk = (vehicle.make || '').toLowerCase();
    var md = (vehicle.model || '').toLowerCase();
    var sm = (vehicle.submodel || '').toLowerCase();
    var exact = false;
    var anySubmodel = false;
    for (var i = 0; i < fitment.length; i++) {
      var f = fitment[i];
      if (
        f.year === y &&
        (f.make || '').toLowerCase() === mk &&
        (f.model || '').toLowerCase() === md
      ) {
        anySubmodel = true;
        if ((f.submodel || '').toLowerCase() === sm || !sm) {
          exact = true;
          break;
        }
      }
    }
    if (exact) return 'fits';
    if (anySubmodel) return 'caution';
    return 'not-fitting';
  }

  function initCardFitment() {
    var hosts = document.querySelectorAll('[data-wishlist-fitment-card]');
    if (!hosts.length) return;
    if (!FITMENT_ENABLED) {
      hosts.forEach(hideFitmentBadge);
      return;
    }
    var vehicle = loadVehicle();
    hosts.forEach(function (host) {
      if (!vehicle) {
        hideFitmentBadge(host);
        return;
      }
      var state = judgeCard(host, vehicle);
      if (!state) {
        hideFitmentBadge(host);
        return;
      }
      renderFitmentBadge(host, state, vehicle, 'card');
    });
  }

  // ドロワー = check_batch を Direct CORS で 1 リクエスト。
  function fetchFitmentBatch(productGids, vehicle) {
    var qs = new URLSearchParams({
      action: 'check_batch',
      year: String(vehicle.year),
      make: vehicle.make,
      model: vehicle.model,
      product_gids: productGids.join(','),
    });
    if (vehicle.submodel) qs.set('submodel', vehicle.submodel);
    // Direct CORS では shop param が必須 (App Proxy 経路では Shopify が自動付与)
    if (FITMENT_SHOP_DOMAIN && FITMENT_API_BASE.indexOf('/apps/') === -1) {
      qs.set('shop', FITMENT_SHOP_DOMAIN);
    }
    return fetch(FITMENT_API_BASE + '?' + qs.toString()).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  // ---- drawer ----
  function svgEl(paths, opts) {
    opts = opts || {};
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', opts.width || '1.75');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    paths.forEach(function (d) {
      var p = document.createElementNS(SVG_NS, 'path');
      p.setAttribute('d', d);
      svg.appendChild(p);
    });
    return svg;
  }

  function buildRemoveButton(variantId) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ark-wishlist-item__remove';
    btn.setAttribute('aria-label', 'ウィッシュリストから削除');
    btn.appendChild(svgEl(['M18 6 6 18', 'M6 6l12 12'], { width: '1.75' }));
    btn.addEventListener('click', function () {
      remove(variantId, null);
    });
    return btn;
  }

  function buildFitmentSlot(productId) {
    // Phase 2.5 適合バッジ用スロット。productId 有りの行のみ check_batch 対象。
    // stub 行 (productId 無し) は data-product-id を持たず batch から除外される。
    var slot = document.createElement('div');
    slot.className = 'ark-wishlist-item__fitment ark-wishlist-fitment';
    slot.setAttribute('data-wishlist-fitment', '');
    if (productId) slot.setAttribute('data-product-id', productId);
    slot.hidden = true;
    var inner = document.createElement('span');
    inner.className = 'ark-wishlist-fitment__inner';
    inner.setAttribute('data-wishlist-fitment-inner', '');
    slot.appendChild(inner);
    return slot;
  }

  function buildItemRow(item) {
    var li = document.createElement('li');
    li.className = 'ark-wishlist-item';

    var media = document.createElement('div');
    media.className = 'ark-wishlist-item__media';
    if (item.image) {
      var img = document.createElement('img');
      img.src = item.image;
      img.alt = '';
      img.loading = 'lazy';
      media.appendChild(img);
    } else {
      media.classList.add('ark-wishlist-item__media--empty');
      media.appendChild(svgEl(['M3 3h18v18H3z', 'm3 15 5-5 4 4 4-4 5 5'], { width: '1.5' }));
    }

    var body = document.createElement('div');
    body.className = 'ark-wishlist-item__body';

    var title;
    if (item.handle) {
      title = document.createElement('a');
      title.href = ROOT_URL + '/products/' + encodeURIComponent(item.handle);
    } else {
      title = document.createElement('span');
    }
    title.className = 'ark-wishlist-item__title';
    title.textContent = item.title || '保存済みアイテム';
    body.appendChild(title);

    // アラート行と同じ出所 (server の JP market 価格) を使う。キャッシュ由来の
    // formatMoney をそのまま出すと、US market で見たときに item だけ USD になる。
    var price = currentPriceText(item);
    if (price) {
      var priceEl = document.createElement('div');
      priceEl.className = 'ark-wishlist-item__price';
      priceEl.textContent = price;
      body.appendChild(priceEl);
    }

    body.appendChild(buildFitmentSlot(item.productId));

    li.appendChild(media);
    li.appendChild(body);
    li.appendChild(buildRemoveButton(item.variantId));

    // Phase 3 価格アラート行 (li に full-width で折り返す)。
    //   ログイン && alertEnabled && productId 有り (stub 除外) のときだけ設定 UI。
    //   ゲストは控えめな「ログインで価格アラート」hint のみ。
    if (ALERT_ENABLED && item.productId) {
      if (LOGGED_IN) {
        li.classList.add('ark-wishlist-item--has-alert');
        li.appendChild(buildAlertRow(item));
      } else {
        li.classList.add('ark-wishlist-item--has-alert');
        li.appendChild(buildGuestAlertHint());
      }
    }
    return li;
  }

  function buildStubRow(variantId) {
    // ログイン時、別端末で保存され表示キャッシュに無い id の最小表示 (Phase 2.5 で Storefront API 解決)
    var li = document.createElement('li');
    li.className = 'ark-wishlist-item';

    var media = document.createElement('div');
    media.className = 'ark-wishlist-item__media ark-wishlist-item__media--empty';
    media.appendChild(svgEl(['M3 3h18v18H3z', 'm3 15 5-5 4 4 4-4 5 5'], { width: '1.5' }));

    var body = document.createElement('div');
    body.className = 'ark-wishlist-item__body';
    var title = document.createElement('span');
    title.className = 'ark-wishlist-item__title';
    title.textContent = '保存済みアイテム';
    var note = document.createElement('span');
    note.className = 'ark-wishlist-item__note';
    note.textContent = '別の端末で保存されました';
    body.appendChild(title);
    body.appendChild(note);
    body.appendChild(buildFitmentSlot());

    li.appendChild(media);
    li.appendChild(body);
    li.appendChild(buildRemoveButton(variantId));
    return li;
  }

  // ---- Phase 3 価格アラート行 (drawer item に折り返す full-width ブロック) ----
  // 3 状態: NONE (未設定・インライン展開) / WATCHING (監視中) / TRIGGERED|NOTIFIED (到達・通知済)。
  // state は alertByVariant[variantId] (server GET が正・set/clear で楽観更新)。
  //   現在 = server の currentJpy (JP market)。取れない時だけ localStorage cache を表示専用で使う。
  //   目標 = server targetJpy もしくは入力値 (整数 JPY)。
  //   あと = 現在(server) - 目標 (accent orange 強調)。範囲検証・プリセット・下落率も server 値のみ。
  function alertIcon(name, cls) {
    var span = document.createElement('span');
    span.className = 'ark-wishlist-alert__icon' + (cls ? ' ' + cls : '');
    var svg;
    if (name === 'trend') {
      // lucide trending-down (下降モチーフ — スパークライン実データは持たない)
      svg = fitmentSvg(
        [
          { tag: 'polyline', attrs: { points: '22 17 13.5 8.5 8.5 13.5 2 7' } },
          { tag: 'polyline', attrs: { points: '16 17 22 17 22 11' } },
        ],
        '1.75'
      );
    } else if (name === 'chevron') {
      svg = fitmentSvg([{ tag: 'polyline', attrs: { points: '6 9 12 15 18 9' } }], '1.75');
    } else {
      // check
      svg = fitmentSvg([{ tag: 'polyline', attrs: { points: '20 6 9 17 4 12' } }], '2');
    }
    span.appendChild(svg);
    return span;
  }

  function makeFigure(label, value, tone) {
    var f = document.createElement('span');
    f.className =
      'ark-wishlist-alert__figure' +
      (tone === 'accent' ? ' ark-wishlist-alert__figure--accent' : '');
    if (label) {
      var l = document.createElement('span');
      l.className = 'ark-wishlist-alert__figure-label';
      l.textContent = label;
      f.appendChild(l);
    }
    var v = document.createElement('span');
    v.className = 'ark-wishlist-alert__figure-value';
    v.textContent = value;
    f.appendChild(v);
    return f;
  }

  function buildGuestAlertHint() {
    // ゲスト: alert 不可 (ログイン必須)。控えめな 1 行 hint のみ。
    var wrap = document.createElement('div');
    wrap.className = 'ark-wishlist-alert ark-wishlist-alert--guest';
    wrap.appendChild(alertIcon('trend'));
    var text = document.createElement('span');
    text.className = 'ark-wishlist-alert__guest-text';
    text.appendChild(document.createTextNode('価格アラートは'));
    var link = document.createElement('a');
    link.className = 'ark-wishlist-alert__guest-link';
    link.href = ALERT_LOGIN_URL;
    link.textContent = 'ログイン';
    text.appendChild(link);
    text.appendChild(document.createTextNode('で設定できます'));
    wrap.appendChild(text);
    return wrap;
  }

  function buildAlertRow(item) {
    var wrap = document.createElement('div');
    wrap.className = 'ark-wishlist-alert';
    wrap.setAttribute('data-ark-wishlist-alert', '');
    renderAlert(wrap, item, false);
    return wrap;
  }

  function renderAlert(container, item, editing) {
    var rec = alertByVariant[item.variantId] || {};
    var state = normalizeAlertState(rec.state);
    var target = rec.targetJpy != null ? rec.targetJpy : null;

    while (container.firstChild) container.removeChild(container.firstChild);
    container.classList.remove(
      'ark-wishlist-alert--none',
      'ark-wishlist-alert--watching',
      'ark-wishlist-alert--reached',
      'is-editing'
    );

    if (state === 'NONE') {
      container.classList.add('ark-wishlist-alert--none');
      container.appendChild(buildAlertHead(container, item, editing));
      if (editing) {
        container.classList.add('is-editing');
        container.appendChild(buildAlertForm(container, item, null));
      }
      return;
    }

    if (editing) {
      // WATCHING / reached の「編集」= フォーム再表示 (現在の目標を pre-fill)
      container.classList.add(
        state === 'WATCHING' ? 'ark-wishlist-alert--watching' : 'ark-wishlist-alert--reached'
      );
      container.classList.add('is-editing');
      container.appendChild(buildAlertForm(container, item, target));
      return;
    }

    if (state === 'WATCHING') {
      container.classList.add('ark-wishlist-alert--watching');
      container.appendChild(buildAlertWatch(container, item, target));
    } else {
      container.classList.add('ark-wishlist-alert--reached');
      container.appendChild(buildAlertReached(container, item, target, state));
    }
  }

  function buildAlertHead(container, item, expanded) {
    var head = document.createElement('button');
    head.type = 'button';
    head.className = 'ark-wishlist-alert__head';
    head.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    head.appendChild(alertIcon('trend'));

    var textWrap = document.createElement('span');
    textWrap.className = 'ark-wishlist-alert__head-text';
    var label = document.createElement('span');
    label.className = 'ark-wishlist-alert__label';
    label.textContent = '価格アラート';
    var desc = document.createElement('span');
    desc.className = 'ark-wishlist-alert__desc';
    desc.textContent = '為替変動で円価格が下がったらメールでお知らせ';
    textWrap.appendChild(label);
    textWrap.appendChild(desc);
    head.appendChild(textWrap);

    var cur = currentPriceText(item);
    if (cur) {
      var curEl = document.createElement('span');
      curEl.className = 'ark-wishlist-alert__current';
      curEl.textContent = '現在 ' + cur;
      head.appendChild(curEl);
    }
    head.appendChild(alertIcon('chevron', 'ark-wishlist-alert__chevron'));

    head.addEventListener('click', function () {
      renderAlert(container, item, !expanded);
      if (!expanded) {
        var input = container.querySelector('[data-alert-input]');
        if (input) input.focus();
      }
    });
    return head;
  }

  function buildAlertForm(container, item, prefillTarget) {
    var form = document.createElement('div');
    form.className = 'ark-wishlist-alert__form';

    var row = document.createElement('div');
    row.className = 'ark-wishlist-alert__form-row';

    var inputId = 'ark-alert-' + item.variantId;
    var label = document.createElement('label');
    label.className = 'ark-wishlist-alert__form-label';
    label.setAttribute('for', inputId);
    label.textContent = '目標価格';

    var inputWrap = document.createElement('span');
    inputWrap.className = 'ark-wishlist-alert__input-wrap';
    var prefix = document.createElement('span');
    prefix.className = 'ark-wishlist-alert__input-prefix';
    prefix.textContent = '¥';
    prefix.setAttribute('aria-hidden', 'true');
    var input = document.createElement('input');
    input.type = 'text';
    input.id = inputId;
    input.className = 'ark-wishlist-alert__input';
    input.setAttribute('data-alert-input', '');
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('placeholder', '希望価格を入力');
    input.setAttribute('aria-label', '目標価格（円）');
    if (prefillTarget != null) input.value = groupDigits(prefillTarget);
    input.addEventListener('input', function () {
      inputWrap.classList.remove('is-invalid');
      input.value = groupDigits(input.value);
    });
    inputWrap.appendChild(prefix);
    inputWrap.appendChild(input);

    row.appendChild(label);
    row.appendChild(inputWrap);

    var currentJpy = serverCurrentJpy(item);
    var cur = currentPriceText(item);
    if (cur) {
      var ref = document.createElement('span');
      ref.className = 'ark-wishlist-alert__ref';
      ref.textContent = '現在 ' + cur;
      row.appendChild(ref);
    }
    form.appendChild(row);

    // プリセット: 現在価格からの割引で候補を出す。現在価格が判らない時は出さない
    // (基準が無いと「現実的な範囲」を示せないため)。
    var presets = presetTargets(currentJpy);
    if (presets.length) {
      var presetWrap = document.createElement('div');
      presetWrap.className = 'ark-wishlist-alert__presets';
      var presetLabel = document.createElement('span');
      presetLabel.className = 'ark-wishlist-alert__presets-label';
      presetLabel.textContent = 'かんたん設定';
      presetWrap.appendChild(presetLabel);

      presets.forEach(function (preset) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ark-wishlist-alert__preset';
        var pct = document.createElement('span');
        pct.className = 'ark-wishlist-alert__preset-pct';
        pct.textContent = '-' + Math.round(preset.ratio * 100) + '%';
        var amount = document.createElement('span');
        amount.className = 'ark-wishlist-alert__preset-amount';
        amount.textContent = formatJpy(preset.value);
        btn.appendChild(pct);
        btn.appendChild(amount);
        btn.setAttribute(
          'aria-label',
          '目標価格を ' +
            formatJpy(preset.value) +
            ' にする（現在から ' +
            Math.round(preset.ratio * 100) +
            '% 下）'
        );
        btn.addEventListener('click', function () {
          input.value = groupDigits(preset.value);
          inputWrap.classList.remove('is-invalid');
          input.focus();
        });
        presetWrap.appendChild(btn);
      });
      form.appendChild(presetWrap);
    }

    var actions = document.createElement('div');
    actions.className = 'ark-wishlist-alert__actions';
    var setBtn = document.createElement('button');
    setBtn.type = 'button';
    setBtn.className = 'ark-wishlist-alert__btn ark-wishlist-alert__btn--set';
    setBtn.textContent = '価格を監視する';
    var cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'ark-wishlist-alert__btn ark-wishlist-alert__btn--cancel';
    cancelBtn.textContent = 'キャンセル';
    actions.appendChild(setBtn);
    actions.appendChild(cancelBtn);
    form.appendChild(actions);

    var hint = document.createElement('p');
    hint.className = 'ark-wishlist-alert__hint';
    hint.textContent =
      currentJpy != null
        ? '表示価格が目標以下になった時に1回通知します（' +
          formatJpy(alertFloorJpy(currentJpy)) +
          ' 〜 現在価格未満で設定できます）'
        : '表示価格が目標以下になった時に1回通知します';
    form.appendChild(hint);

    setBtn.addEventListener('click', function () {
      var target = parseTargetJpy(input.value);
      if (target == null) {
        inputWrap.classList.add('is-invalid');
        toast(MSG_ALERT_INVALID);
        input.focus();
        return;
      }
      // 現在価格はクリック時に読み直す。フォーム生成時の値で固定すると、直前の 400 応答で
      // 更新された最新価格を無視して「サーバなら通る目標」を弾いてしまう。
      var latestJpy = serverCurrentJpy(item);
      if (latestJpy != null) {
        if (target >= latestJpy) {
          inputWrap.classList.add('is-invalid');
          toast(MSG_ALERT_NOT_BELOW);
          input.focus();
          return;
        }
        var floor = alertFloorJpy(latestJpy);
        if (target < floor) {
          inputWrap.classList.add('is-invalid');
          toast(floorMessage(floor));
          input.focus();
          return;
        }
      }
      setAlertRequest(container, item, target, form, setBtn, cancelBtn, latestJpy);
    });
    cancelBtn.addEventListener('click', function () {
      renderAlert(container, item, false);
    });

    return form;
  }

  function buildAlertViewActions(container, item, editLabel) {
    var actions = document.createElement('div');
    actions.className = 'ark-wishlist-alert__actions ark-wishlist-alert__actions--view';
    var editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'ark-wishlist-alert__btn ark-wishlist-alert__btn--edit';
    editBtn.textContent = editLabel || '編集';
    editBtn.addEventListener('click', function () {
      renderAlert(container, item, true);
      var input = container.querySelector('[data-alert-input]');
      if (input) input.focus();
    });
    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'ark-wishlist-alert__btn ark-wishlist-alert__btn--clear';
    clearBtn.textContent = '解除';
    clearBtn.addEventListener('click', function () {
      clearAlertRequest(container, item);
    });
    actions.appendChild(editBtn);
    actions.appendChild(clearBtn);
    return actions;
  }

  // 「設定時から下がった」ことだけを出す行。値上がり時は要素ごと作らない (購買意欲を削がない)。
  // 基準・現在値とも server 由来のみ (キャッシュ由来だと保存時の古い値で偽の下落を出す)。
  function buildAlertDropLine(item, rec) {
    var drop = dropSinceStart(rec && rec.startJpy, serverCurrentJpy(item));
    if (!drop) return null;
    var line = document.createElement('p');
    line.className = 'ark-wishlist-alert__drop';
    var icon = alertIcon('trend', 'ark-wishlist-alert__drop-icon');
    line.appendChild(icon);
    var text = document.createElement('span');
    // 0.05% 未満は toFixed(1) が "0.0" になり「0.0% 下がりました」と矛盾するので、
    // その場合は率を出さず金額だけで伝える。
    text.textContent =
      drop.percent >= 0.05
        ? '設定時から ' +
          drop.percent.toFixed(1) +
          '% 下がりました（' +
          formatJpy(drop.amount) +
          '）'
        : '設定時から ' + formatJpy(drop.amount) + ' 下がりました';
    line.appendChild(text);
    return line;
  }

  function buildAlertWatch(container, item, target) {
    var view = document.createElement('div');
    view.className = 'ark-wishlist-alert__view';

    var top = document.createElement('div');
    top.className = 'ark-wishlist-alert__view-top';
    top.appendChild(alertIcon('trend'));
    var label = document.createElement('span');
    label.className = 'ark-wishlist-alert__label';
    label.textContent = '価格アラート';
    top.appendChild(label);
    var badge = document.createElement('span');
    badge.className = 'ark-wishlist-alert__badge ark-wishlist-alert__badge--watching';
    badge.textContent = '監視中';
    top.appendChild(badge);
    view.appendChild(top);

    var figures = document.createElement('div');
    figures.className = 'ark-wishlist-alert__figures';
    var curStr = currentPriceText(item);
    if (curStr) figures.appendChild(makeFigure('現在', curStr, null));
    if (target != null) figures.appendChild(makeFigure('目標', formatJpy(target), null));
    var currentJpy = serverCurrentJpy(item);
    if (currentJpy != null && target != null) {
      var diff = currentJpy - target;
      if (diff > 0) {
        figures.appendChild(makeFigure('あと', formatJpy(diff), 'accent'));
      } else {
        figures.appendChild(makeFigure(null, 'まもなく到達', 'accent'));
      }
    }
    view.appendChild(figures);

    var dropLine = buildAlertDropLine(item, alertByVariant[item.variantId]);
    if (dropLine) view.appendChild(dropLine);

    view.appendChild(buildAlertViewActions(container, item, '編集'));
    return view;
  }

  function buildAlertReached(container, item, target, state) {
    var view = document.createElement('div');
    view.className = 'ark-wishlist-alert__view';

    var top = document.createElement('div');
    top.className = 'ark-wishlist-alert__view-top';
    top.appendChild(alertIcon('check', 'ark-wishlist-alert__icon--success'));
    var label = document.createElement('span');
    label.className = 'ark-wishlist-alert__label';
    label.textContent = '価格アラート';
    top.appendChild(label);
    var badge = document.createElement('span');
    badge.className = 'ark-wishlist-alert__badge ark-wishlist-alert__badge--reached';
    badge.textContent = '目標価格に到達';
    top.appendChild(badge);
    view.appendChild(top);

    var body = document.createElement('div');
    body.className = 'ark-wishlist-alert__reached-body';
    var primary = document.createElement('span');
    primary.className = 'ark-wishlist-alert__reached-primary';
    var sub = document.createElement('span');
    sub.className = 'ark-wishlist-alert__reached-sub';
    if (state === 'NOTIFIED') {
      primary.textContent = 'メールで通知済み';
      sub.textContent =
        target != null ? '目標 ' + formatJpy(target) + ' に到達しました' : '目標価格に到達しました';
    } else {
      primary.textContent = '目標価格に到達';
      sub.textContent =
        target != null
          ? '目標 ' + formatJpy(target) + ' — まもなくメールでお知らせします'
          : 'まもなくメールでお知らせします';
    }
    body.appendChild(primary);
    body.appendChild(sub);
    view.appendChild(body);

    view.appendChild(buildAlertViewActions(container, item, '編集'));
    return view;
  }

  // 400 の詳細 (error コード + 境界値) を読む。body が無い / 壊れていても落とさない。
  function readAlertError(res) {
    return res
      .json()
      .then(function (body) {
        return body && typeof body === 'object' ? body : {};
      })
      .catch(function () {
        return {};
      });
  }

  // 設定: pending (楽観ではなく確定待ち — server が価格取得に失敗 503 / 範囲外 400 し得るため)。
  //   201/200 → WATCHING 表示。503/400/404/その他 → トースト + フォーム維持 (ロールバック)。
  function setAlertRequest(container, item, target, form, setBtn, cancelBtn, currentJpy) {
    form.classList.add('is-pending');
    setBtn.disabled = true;
    cancelBtn.disabled = true;
    var origLabel = setBtn.textContent;
    setBtn.textContent = '設定中…';
    var restore = function () {
      form.classList.remove('is-pending');
      setBtn.disabled = false;
      cancelBtn.disabled = false;
      setBtn.textContent = origLabel;
    };
    apiSetAlert(item.variantId, target)
      .then(function (res) {
        if (res.ok) {
          // startJpy = 設定時の表示 JPY。server も同じ値を保持するが、次の reconcile を
          // 待たずに「設定時から −X%」を出せるよう手元にも入れる。
          alertByVariant[item.variantId] = {
            state: 'WATCHING',
            targetJpy: target,
            startJpy: currentJpy != null ? currentJpy : null,
          };
          renderAlert(container, item, false);
          return;
        }
        restore();
        if (res.status === 503) {
          toast(MSG_ALERT_BASEPRICE);
          return;
        }
        if (res.status === 400) {
          // サーバが境界値を返すので、それを使って具体的に伝える。
          readAlertError(res).then(function (body) {
            var code = body.error;
            if (code === 'target_not_below_current') {
              toast(MSG_ALERT_NOT_BELOW);
            } else if (code === 'target_below_floor' && body.floorJpy != null) {
              toast(floorMessage(body.floorJpy));
            } else {
              toast(MSG_ALERT_INVALID);
            }
            // server の現在価格が判ったら表示にも反映する (キャッシュとのズレを解消)。
            if (body.currentJpy != null && isFinite(body.currentJpy)) {
              currentJpyByVariant[item.variantId] = body.currentJpy;
            }
          });
          return;
        }
        toast(MSG_ALERT_FAIL);
      })
      .catch(function () {
        restore();
        toast(MSG_ALERT_FAIL);
      });
  }

  // 解除: 楽観的に NONE 表示 → 失敗は直前状態へロールバック + トースト。
  function clearAlertRequest(container, item) {
    var prev = alertByVariant[item.variantId] || { state: 'NONE', targetJpy: null };
    alertByVariant[item.variantId] = { state: 'NONE', targetJpy: null };
    renderAlert(container, item, false);
    apiClearAlert(item.variantId)
      .then(function (res) {
        if (!res.ok) {
          alertByVariant[item.variantId] = prev;
          renderAlert(container, item, false);
          toast(MSG_ALERT_CLEAR_FAIL);
        }
      })
      .catch(function () {
        alertByVariant[item.variantId] = prev;
        renderAlert(container, item, false);
        toast(MSG_ALERT_CLEAR_FAIL);
      });
  }

  function focusableIn(root) {
    return root.querySelectorAll(
      'button:not([disabled]):not([hidden]), [href], select:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function DrawerWidget(container) {
    this.container = container;
    this.modal = container.querySelector('[data-ark-wishlist-modal]');
    this.backdrop = container.querySelector('[data-ark-wishlist-backdrop]');
    this.closeBtn = container.querySelector('[data-ark-wishlist-close]');
    this.list = container.querySelector('[data-ark-wishlist-list]');
    this.empty = container.querySelector('[data-ark-wishlist-empty]');
    this.countLabel = container.querySelector('[data-ark-wishlist-count-label]');
    this.fitmentGuidance = container.querySelector('[data-ark-wishlist-fitment-guidance]');
    this.fitmentGuidanceBtn = container.querySelector('[data-ark-wishlist-vehicle-request]');
    this._lastFocus = null;
    this._modalAbort = null;
    this.bind();
    this.render();
  }

  DrawerWidget.prototype.bind = function () {
    var self = this;
    if (this.closeBtn)
      this.closeBtn.addEventListener('click', function () {
        self.close();
      });
    if (this.backdrop)
      this.backdrop.addEventListener('click', function () {
        self.close();
      });
    window.addEventListener(OPEN_EVENT, function () {
      self.open();
    });
    window.addEventListener(CHANGED_EVENT, function () {
      self.render();
    });
    // 開いている間に garage 車両が変わったら適合を再評価 (vehicleKey が変われば再 fetch)。
    window.addEventListener(VEHICLE_CHANGED_EVENT, function () {
      self.evaluateFitment();
    });
    if (this.fitmentGuidanceBtn) {
      this.fitmentGuidanceBtn.addEventListener('click', function () {
        window.dispatchEvent(new CustomEvent(VEHICLE_CHANGE_REQUEST_EVENT));
      });
    }
  };

  DrawerWidget.prototype.open = function () {
    if (!this.modal || !this.modal.hidden) return;
    this._lastFocus = document.activeElement;
    this.render();
    this.modal.hidden = false;
    // render() の適合評価は modal.hidden 中は skip されるため、open 確定後に 1 回叩く。
    this.evaluateFitment();
    window.__arkScrollLock.lock();

    var self = this;
    this._modalAbort = new AbortController();
    document.addEventListener(
      'keydown',
      function (e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          self.close();
          return;
        }
        if (e.key === 'Tab') {
          var f = focusableIn(self.modal);
          if (!f.length) return;
          var first = f[0];
          var last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      },
      { signal: this._modalAbort.signal }
    );

    var focusFirst = focusableIn(this.modal)[0];
    if (focusFirst) focusFirst.focus();
  };

  DrawerWidget.prototype.close = function () {
    if (!this.modal || this.modal.hidden) return;
    this.modal.hidden = true;
    window.__arkScrollLock.unlock();
    if (this._modalAbort) {
      this._modalAbort.abort();
      this._modalAbort = null;
    }
    if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
  };

  DrawerWidget.prototype.render = function () {
    if (!this.list) return;
    var cache = loadCache();
    var cacheIds = new Set(
      cache.map(function (i) {
        return i.variantId;
      })
    );
    var stubs = [];
    if (LOGGED_IN) {
      savedIds.forEach(function (vid) {
        if (!cacheIds.has(vid)) stubs.push(vid);
      });
    }
    var total = cache.length + stubs.length;

    while (this.list.firstChild) this.list.removeChild(this.list.firstChild);

    if (this.countLabel) {
      if (total > 0) {
        this.countLabel.hidden = false;
        this.countLabel.textContent = total + ' 件保存済み';
      } else {
        this.countLabel.hidden = true;
      }
    }

    if (total === 0) {
      this.list.hidden = true;
      if (this.empty) this.empty.hidden = false;
      return;
    }

    this.list.hidden = false;
    if (this.empty) this.empty.hidden = true;

    var self = this;
    cache.forEach(function (item) {
      self.list.appendChild(buildItemRow(item));
    });
    stubs.forEach(function (vid) {
      self.list.appendChild(buildStubRow(vid));
    });

    // 行を作り直したので適合バッジを貼り直す (open 中のみ実評価)。
    this.evaluateFitment();
  };

  DrawerWidget.prototype.setFitmentGuidance = function (show) {
    if (!this.fitmentGuidance) return;
    this.fitmentGuidance.hidden = !(show && FITMENT_ENABLED);
  };

  DrawerWidget.prototype.hideAllItemFitment = function () {
    if (!this.list) return;
    this.list.querySelectorAll('[data-wishlist-fitment]').forEach(hideFitmentBadge);
  };

  DrawerWidget.prototype.applyFitmentResults = function (results, vehicle) {
    if (!this.list) return;
    this.list.querySelectorAll('[data-wishlist-fitment][data-product-id]').forEach(function (slot) {
      var pid = slot.getAttribute('data-product-id');
      var r = results['gid://shopify/Product/' + pid];
      if (!r) {
        hideFitmentBadge(slot);
        return;
      }
      var state = reasonToState(r.reason, r.fits);
      if (!state) {
        hideFitmentBadge(slot);
        return;
      }
      renderFitmentBadge(slot, state, vehicle, 'drawer');
    });
  };

  // ドロワー適合評価: 開時 + open 中の vehicle 変更 + 行再構築で呼ばれる。
  //   未選択 → 上部に 1 誘導行、item バッジ無し。
  //   選択済 → vehicleKey cache hit なら再 fetch せず適用、miss なら check_batch を 1 リクエスト。
  //   degrade (batch 失敗 / gid 無し) → 全 item バッジ非表示 (誤情報を出さない)。
  DrawerWidget.prototype.evaluateFitment = function () {
    if (!this.modal || this.modal.hidden) return; // 開いている間だけ叩く (storm 回避)
    var self = this;
    if (!FITMENT_ENABLED) {
      this.setFitmentGuidance(false);
      this.hideAllItemFitment();
      return;
    }
    var vehicle = loadVehicle();
    if (!vehicle) {
      this.setFitmentGuidance(true);
      this.hideAllItemFitment();
      return;
    }
    this.setFitmentGuidance(false);

    var slots = this.list
      ? this.list.querySelectorAll('[data-wishlist-fitment][data-product-id]')
      : [];
    if (!slots.length) return;

    var gids = [];
    slots.forEach(function (slot) {
      var pid = slot.getAttribute('data-product-id');
      if (pid) gids.push('gid://shopify/Product/' + pid);
    });
    if (!gids.length) return;

    var vk = vehicleKey(vehicle); // race guard 用: 車両同一性
    // cache key に要求 product 集合を含める。同一 vehicle でも item 追加で集合が変われば
    // 別キーになり、旧集合 (新商品を含まない) の結果を誤って再利用してバッジを隠さない。
    var cacheKey = vk + '|' + gids.slice().sort().join(',');
    var cached = fitmentBatchCache[cacheKey];
    if (cached) {
      this.applyFitmentResults(cached, vehicle);
      return;
    }

    fetchFitmentBatch(gids, vehicle)
      .then(function (json) {
        var results = (json && json.results) || {};
        fitmentBatchCache[cacheKey] = results;
        // fetch 中に車両が変わっていたら適用しない (race guard は車両同一性 vk で判定)。
        var cur = loadVehicle();
        if (cur && vehicleKey(cur) === vk) self.applyFitmentResults(results, cur);
      })
      .catch(function () {
        // 古い車両リクエストの reject で最新車両のバッジを消さない。
        // 選択中車両がこのリクエストの車両と一致するときだけ degrade する。
        var cur = loadVehicle();
        if (cur && vehicleKey(cur) === vk) self.hideAllItemFitment();
      });
  };

  function initDrawer() {
    var el = document.querySelector('[data-ark-wishlist]');
    if (el && !el._arkWishlistDrawer) el._arkWishlistDrawer = new DrawerWidget(el);
  }

  function initHeader() {
    document.querySelectorAll('[data-custom-wishlist-open]').forEach(function (btn) {
      if (btn._arkWishlistOpenBound) return;
      btn._arkWishlistOpenBound = true;
      btn.addEventListener('click', function () {
        window.dispatchEvent(new CustomEvent(OPEN_EVENT));
      });
    });
  }

  // ---- load / merge / reconcile ----
  // merged フラグはサーバへ push 済みのゲスト cache 集合 (variantId) を署名として保持する。
  // 単純な session フラグ ('1') だと logout→ゲスト item 追加→再login を跨いで残り、
  // alreadyMerged() が skip した後の GET reconcile が未マージ item を localStorage から消す。
  // 署名にすることで (a) 未署名のゲスト追加があれば再 merge され、(b) 同一集合の再訪では
  // merge を 1 回に絞れる (merge はサーバ側で idempotent なので再送は skip されるだけ)。
  function cacheSignatureIds() {
    return loadCache()
      .map(function (i) {
        return i.variantId;
      })
      .sort();
  }

  function markMerged() {
    try {
      sessionStorage.setItem(MERGED_FLAG, JSON.stringify(cacheSignatureIds()));
    } catch {
      /* ignore */
    }
  }

  function alreadyMerged() {
    try {
      var raw = sessionStorage.getItem(MERGED_FLAG);
      if (!raw) return false;
      var merged;
      try {
        merged = JSON.parse(raw);
      } catch {
        return false; // 旧形式 ('1') / 破損 → 未 merge 扱いで再 merge (idempotent)
      }
      if (!Array.isArray(merged)) return false;
      var mergedSet = new Set(merged);
      // 現在の cache が全て署名に含まれる (= 新規ゲスト追加が無い) ときだけ merge 済みとみなす。
      return cacheSignatureIds().every(function (vid) {
        return mergedSet.has(vid);
      });
    } catch {
      return false;
    }
  }

  function reconcileWithServer(serverData) {
    var serverIds = [];
    // Phase 3: server GET は item 毎に alertState / targetJpy / startJpy / currentJpy を同梱する。
    // 価格は JP market の実価格なので、表示・比較ともキャッシュより server を優先する。
    var nextAlerts = {};
    var nextPrices = {};
    if (Array.isArray(serverData)) {
      serverData.forEach(function (d) {
        var vid = toIdString(d && d.variantId);
        if (!vid) return;
        serverIds.push(vid);
        var st = normalizeAlertState(d && d.alertState);
        var tj = parseServerTargetJpy(d && d.targetJpy);
        var sj = parseServerTargetJpy(d && d.startJpy);
        var cj = parseServerTargetJpy(d && d.currentJpy);
        if (cj != null) nextPrices[vid] = cj;
        if (st !== 'NONE' || tj != null) {
          nextAlerts[vid] = { state: st, targetJpy: tj, startJpy: sj };
        }
      });
    }
    var serverSet = new Set(serverIds);
    savedIds = serverSet;
    alertByVariant = nextAlerts;
    currentJpyByVariant = nextPrices;
    // 表示キャッシュを server の id 集合に整える (別端末で削除された分を掃除)
    saveCache(
      loadCache().filter(function (i) {
        return serverSet.has(i.variantId);
      })
    );
    emitChanged();
  }

  function initState() {
    seedFromCache();
    emitChanged();

    if (!LOGGED_IN) return;

    var cache = loadCache();
    var mergeNeeded = cache.length > 0 && !alreadyMerged();
    // merge が要るのに成功しなかった場合、後続の GET reconcile は未マージのゲスト item を
    // サーバに無いものとして localStorage から全消去してしまう。これを防ぐため mergeOk を
    // 追跡し、merge が成功しなかったときは reconcile を止めてローカル cache を保持する
    // (markMerged していないので次回起動で merge をリトライできる)。
    var mergeOk = !mergeNeeded;
    var mergePromise = Promise.resolve();
    if (mergeNeeded) {
      var items = cache.map(function (i) {
        return { productId: i.productId, variantId: i.variantId };
      });
      mergePromise = apiMerge(items)
        .then(function (res) {
          if (res.ok) {
            markMerged();
            mergeOk = true;
          }
        })
        .catch(function () {
          /* merge 失敗 — reconcile を抑止しローカル cache を保持 (次回リトライ) */
        });
    }

    mergePromise
      .then(function () {
        return apiGet();
      })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (json) {
        // merge が成功しなかった場合は reconcile しない (未マージのゲスト item が
        // サーバに無く、全消去されるため)。ローカル cache を維持して次回に委ねる。
        if (!mergeOk) return;
        if (json && json.success && Array.isArray(json.data)) {
          reconcileWithServer(json.data);
        }
        // 失敗時はキャッシュ由来の savedIds を維持 (楽観フォールバック)
      })
      .catch(function () {
        DBG('initState server sync failed — keeping local cache state');
      });
  }

  // ---- boot ----
  function boot() {
    bindDelegatedClick();
    initButtons();
    observeInsertedButtons();
    initHeader();
    initDrawer();
    initCardFitment();
    window.addEventListener(CHANGED_EVENT, function () {
      syncAllButtons();
      updateBadges();
    });
    // garage 車両が変わったらコレクションカードの適合バッジを再判定 (SSR 0-network)。
    window.addEventListener(VEHICLE_CHANGED_EVENT, initCardFitment);
    initState();
  }

  // ------- public API (window.ark.wishlist.*) -------
  // custom-fitment-garage の window.ark.garage と同じ流儀。メンバーランチャーなど
  // 別 widget から触るときは必ずここを通す。書き手を 1 本に保つため、localStorage の
  // 直接書き換えはしない。remove は楽観更新 → サーバ DELETE → 失敗時ロールバック +
  // トーストまで既存経路が面倒を見る。
  window.ark = window.ark || {};
  window.ark.wishlist = {
    /** 表示キャッシュの配列 (variantId / handle / title / image / price / currency)。 */
    list: function () {
      return loadCache();
    },
    /**
     * 描画用の全件。キャッシュに無い server 保存分 (別端末で ♡ した variant) を
     * stub として補う。ドロワーと同じ数え方にするための正本で、list() だけを見ると
     * 別端末保存分が丸ごと落ちる。stub は { variantId, stub: true } のみを持つ。
     */
    entries: function () {
      var cache = loadCache();
      var seen = new Set(
        cache.map(function (i) {
          return i.variantId;
        })
      );
      var out = cache.slice();
      if (LOGGED_IN) {
        savedIds.forEach(function (vid) {
          if (!seen.has(vid)) out.push({ variantId: vid, stub: true });
        });
      }
      return out;
    },
    /**
     * 価格の整形。price は Shopify の最小単位 (¥1 でも 100 倍) なので、
     * 呼び出し側で素の数値を通貨記号に足すと 100 倍で表示される。
     */
    format: function (price, currency) {
      return formatMoney(price, currency);
    },
    /** variantId で 1 件削除。未保存 id は no-op。 */
    remove: function (variantId) {
      remove(variantId, null);
    },
    /** 保存件数 (server 保存分を含む)。 */
    count: function () {
      return savedIds.size;
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
